export type FadeSliderOptions = {
	slidesSelector?: string;
	slideSelector?: string;
	prevSelector?: string;
	nextSelector?: string;
	paginationContainerSelector?: string;
	autoplay?: boolean;
	interval?: number;
	pauseOnHover?: boolean;
	pagination?: boolean;
};

export class FadeSlider {
	private root: HTMLElement;
	private slidesWrap: HTMLElement;
	private slides: HTMLElement[];
	private prev?: HTMLButtonElement | null;
	private next?: HTMLButtonElement | null;
	private pagination?: HTMLElement | null;
	private dots: HTMLButtonElement[] = [];
	private idx = 0;
	private timer: number | null = null;
	private opts: Required<FadeSliderOptions>;
	private userPaused = false;

	constructor(root: HTMLElement, options: FadeSliderOptions = {}) {
		this.root = root;

		this.opts = {
			slidesSelector: '.fade-slider__slides',
			slideSelector: '.fade-slider__slide',
			prevSelector: '.fade-slider__prev',
			nextSelector: '.fade-slider__next',
			paginationContainerSelector: '.fade-slider__pagination',
			autoplay: false,
			interval: 6000,
			pauseOnHover: true,
			pagination: false,
			...options,
		};

		const wrap = this.root.querySelector(this.opts.slidesSelector);
		if (!wrap) {
			throw new Error('Slides wrapper not found');
		}
		this.slidesWrap = wrap as HTMLElement;
		this.slides = Array.from(this.slidesWrap.querySelectorAll(this.opts.slideSelector)) as HTMLElement[];

		this.prev = this.root.querySelector(this.opts.prevSelector) as HTMLButtonElement | null;
		this.next = this.root.querySelector(this.opts.nextSelector) as HTMLButtonElement | null;

		this.idx = this.findActiveIndex() ?? 0;
		this.userPaused = !this.opts.autoplay;

		this.initA11y();
		this.buildPagination();
		this.bindEvents();
		this.show(this.idx);
		this.autoplayStart();
	}

	nextSlide = () => this.show(this.idx + 1);
	prevSlide = () => this.show(this.idx - 1);
	goTo = (index: number) => this.show(index);
	stop = () => {
		this.userPaused = true;
		this.autoplayStop();
		this.opts.autoplay = false;
	};
	start = () => {
		this.userPaused = false;
		this.opts.autoplay = true;
		this.autoplayStart();
	};
	isAutoplaying = () => this.timer !== null;

	get isPaused() {
		return this.userPaused;
	}

	private initA11y() {
		this.root.setAttribute('role', 'region');
		this.root.setAttribute('aria-roledescription', 'carousel');
		if (!this.root.getAttribute('aria-label')) {
			this.root.setAttribute('aria-label', 'Slider');
		}

		this.slidesWrap.setAttribute('role', 'group');
		this.slidesWrap.setAttribute('aria-roledescription', 'slides');
		this.slidesWrap.setAttribute('aria-live', this.opts.autoplay ? 'off' : 'polite');

		const len = this.slides.length;
		this.slides.forEach((s, i) => {
			const active = i === this.idx;
			s.classList.toggle('is-active', active);
			s.setAttribute('aria-hidden', active ? 'false' : 'true');
			this.slidesWrap.setAttribute('aria-label', `Slide ${this.idx + 1} von ${len}`);
		});
	}

	private updateA11yState() {
		const len = this.slides.length;

		this.slides.forEach((s, i) => {
			const active = i === this.idx;
			s.setAttribute('aria-hidden', active ? 'false' : 'true');
			if (active) {
				s.removeAttribute('inert');
			} else {
				s.setAttribute('inert', '');
			}
		});

		this.dots.forEach((d, i) => {
			const active = i === this.idx;
			d.setAttribute('aria-selected', active ? 'true' : 'false');
			d.tabIndex = active ? 0 : -1;
			d.classList.toggle('is-active', active);
		});

		this.slidesWrap.setAttribute('aria-label', `Slide ${this.idx + 1} von ${len}`);
	}

	private buildPagination() {
		if (!this.opts.pagination) {
			return;
		}

		this.pagination = this.root.querySelector(this.opts.paginationContainerSelector) as HTMLElement | null;
		if (!this.pagination) {
			return;
		} else {
			this.pagination.innerHTML = '';
		}

		this.dots = this.slides.map((_, i) => {
			const btn = document.createElement('button');
			btn.type = 'button';
			btn.className = 'stage-home__dot';
			btn.setAttribute('aria-label', `Slide ${i + 1}`);
			btn.addEventListener('click', () => this.show(i));
			this.pagination!.appendChild(btn);
			return btn;
		});
	}

	private bindEvents() {
		this.prev?.addEventListener('click', this.prevSlide);
		this.next?.addEventListener('click', this.nextSlide);

		this.root.addEventListener('keydown', (e) => {
			if (e.key === 'ArrowLeft') {
				this.prevSlide();
			}
			if (e.key === 'ArrowRight') {
				this.nextSlide();
			}
		});

		if (this.opts.pauseOnHover) {
			this.root.addEventListener('mouseenter', () => this.autoplayStop());
			this.root.addEventListener('mouseleave', () => {
				if (!this.userPaused) {
					this.autoplayStart();
				}
			});
		}

		if (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
			this.opts.autoplay = false;
			this.autoplayStop();
		}
	}

	private findActiveIndex(): number | null {
		const idx = this.slides.findIndex((s) => s.classList.contains('is-active'));
		return idx >= 0 ? idx : null;
	}

	private show(index: number) {
		const len = this.slides.length;
		if (!len) {
			return;
		}
		this.idx = ((index % len) + len) % len;

		this.slides.forEach((s, i) => s.classList.toggle('is-active', i === this.idx));

		const pauseBtn = this.root.querySelector('.media-text__pause') as HTMLElement;
		if (pauseBtn) {
			const activeSlide = this.slides[this.idx];
			const video = activeSlide.querySelector('video') as HTMLVideoElement | null;
			const iframe = activeSlide.querySelector('iframe') as HTMLIFrameElement | null;

			if (video) {
				pauseBtn.style.display = 'inline-flex';
				pauseBtn.setAttribute('data-lightbox', video.getAttribute('src') || '');
				pauseBtn.setAttribute('data-type', 'video');
			} else if (iframe) {
				pauseBtn.style.display = 'inline-flex';
				pauseBtn.setAttribute('data-lightbox', iframe.getAttribute('src') || '');
				pauseBtn.setAttribute('data-type', 'iframe');
			} else {
				pauseBtn.style.display = 'none';
				pauseBtn.removeAttribute('data-lightbox');
				pauseBtn.removeAttribute('data-type');
			}
		}

		this.dots.forEach((d, i) => d.classList.toggle('is-active', i === this.idx));
		this.slidesWrap.setAttribute('aria-label', `Slide ${this.idx + 1} von ${len}`);
		this.updateA11yState();
	}

	private autoplayStart() {
		if (this.userPaused || !this.opts.autoplay || this.timer) {
			return;
		}
		this.timer = window.setInterval(() => this.nextSlide(), this.opts.interval);
	}

	private autoplayStop() {
		if (this.timer) {
			window.clearInterval(this.timer);
			this.timer = null;
		}
	}
}
